import { Controller } from '@hotwired/stimulus'
import { loadApexCharts } from '../utils/chart_loader'

export default class extends Controller {
  static values = {
    data: Array,
    name: String,
    categories: Array,
    colors: { type: Array, default: [] },
    textColors: { type: Array, default: [] },
    quota: { type: Array, default: [] },
    bonus: { type: Array, default: [] },
  }

  connect() {
    this.initializeChart()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async initializeChart() {
    if (this.chart) {
      this.chart.destroy()
    }

    const ApexCharts = (await loadApexCharts()).default

    const barColors = this.colorsValue.length > 0 ? this.colorsValue : ['#CA8A04']
    // A quota array turns the chart into a stacked column + step-line: each bar
    // splits into the portion covered by the plan and the excess above that
    // bucket's limit, while the line draws the limit in force at each bucket.
    const hasQuota = this.quotaValue.some((quota) => quota !== null && quota !== undefined)

    // Bonus units are free, so they sit on top of the bar: its height keeps the
    // real volume while the covered/excess split only judges what is counted.
    const bonus = this.dataValue.map((_, index) => this.bonusValue[index] || 0)
    const hasBonus = bonus.some((units) => units > 0)
    const stacked = hasQuota || hasBonus

    // Quota semantics: null = not contracted, 0 = unlimited — neither draws a
    // limit line nor splits the bar; only positive quotas do.
    const quotaLine = this.quotaValue.map((quota) => (quota > 0 ? quota : null))
    const covered = this.dataValue.map((count, index) =>
      quotaLine[index] ? Math.min(count, quotaLine[index]) : count
    )
    const excess = this.dataValue.map((count, index) =>
      quotaLine[index] ? Math.max(count - quotaLine[index], 0) : 0
    )

    // ApexCharts 4.x rounds the corners of the wrong stacked segments: only the
    // stack's first positive series and the last positive series overall (which
    // the quota line would be) get corners, so the excess cap renders square and
    // detached. The invisible epsilon base pushes the covered series into the
    // unrounded middle slot, and the quota line dodges the census by carrying
    // negated values on a mirrored, reversed, hidden axis.
    const spacer = this.dataValue.map(() => 0.001)
    const totals = this.dataValue.map((count, index) => count + bonus[index])
    const axisMax = Math.ceil(Math.max(...totals, ...quotaLine.filter(Boolean), 1) * 1.05)

    const columns = [
      { name: 'Base', type: 'column', data: spacer, color: 'rgba(255,255,255,0)' },
      { name: this.nameValue, type: 'column', data: covered, color: barColors[0] },
      { name: 'Acima da franquia', type: 'column', data: excess, color: barColors[1] || '#DC2626' },
      ...(hasBonus ? [{ name: 'Bonificados', type: 'column', data: bonus, color: '#D1D5DB' }] : []),
    ]

    const series = stacked
      ? [
          ...columns.map(({ name, type, data }) => ({ name, type, data })),
          { name: 'Franquia contratada', type: 'line', data: quotaLine.map((quota) => (quota === null ? null : -quota)) },
        ]
      : [{ name: this.nameValue, data: this.dataValue }]

    const colors = stacked ? [...columns.map(({ color }) => color), '#9CA3AF'] : barColors
    const columnStrokes = columns.map(() => 0)

    const options = {
      colors,
      chart: {
        type: stacked ? 'line' : 'bar',
        stacked,
        toolbar: { show: false },
        height: '100%',
        parentHeightOffset: 0,
        fontFamily: 'Inter, system-ui, sans-serif',
        animations: { enabled: false },
      },
      title: { text: undefined },
      series,
      stroke: stacked
        ? { width: [...columnStrokes, 2.5], curve: 'stepline', dashArray: [...columnStrokes, 6] }
        : {},
      markers: { size: 0 },
      xaxis: {
        categories: this.categoriesValue,
        labels: {
          style: { fontSize: '12px', fontWeight: '500', colors: '#6B7280' },
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: stacked
        ? [
            { seriesName: columns.map(({ name }) => name), show: false, min: 0, max: axisMax },
            { seriesName: 'Franquia contratada', show: false, min: -axisMax, max: 0, reversed: true },
          ]
        : { show: false },
      grid: { show: false, padding: { left: 4, right: 4, top: 40 } },
      // In the stacked variant per-segment labels are off, but offsetY still
      // feeds the stacked total's vertical position — keep it when editing.
      dataLabels: {
        enabled: !stacked,
        offsetY: -22,
        style: { fontSize: '15px', fontWeight: '700', colors: ['#111827'] },
        formatter: (val) => (val > 0 ? val : ''),
      },
      plotOptions: {
        bar: {
          columnWidth: '68%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
          borderRadiusWhenStacked: 'last',
          dataLabels: {
            position: 'top',
            total: {
              enabled: stacked,
              style: { fontSize: '15px', fontWeight: '700', color: '#111827' },
              formatter: (val) => {
                const total = Math.round(val)
                return total > 0 ? total : ''
              },
            },
          },
        },
      },
      legend: { show: false },
      // Mixed column+line charts misalign the shared tooltip's hover index
      // (off-by-one), so the stacked variant only fires on the hovered bar
      // itself (intersect) and renders its own tooltip from that index.
      tooltip: stacked
        ? {
            shared: false,
            intersect: true,
            custom: ({ dataPointIndex }) => {
              const label = this.categoriesValue[dataPointIndex]
              const count = this.dataValue[dataPointIndex]
              const units = bonus[dataPointIndex]
              const quota = this.quotaValue[dataPointIndex]
              const within = quota > 0 ? Math.min(count, quota) : count
              const over = quota > 0 ? Math.max(count - quota, 0) : 0
              const quotaLine =
                quota === null || quota === undefined
                  ? 'Sem franquia vigente no mês'
                  : quota === 0
                    ? 'Franquia ilimitada'
                    : `Franquia contratada: <strong>${quota}</strong>`
              const row = (text, color) =>
                '<div style="display:flex;align-items:center;gap:6px;">' +
                (color ? `<span style="width:8px;height:8px;border-radius:2px;background:${color};"></span>` : '') +
                `<span>${text}</span></div>`
              const breakdown = [
                quota > 0 ? row(`Dentro da franquia: <strong>${within}</strong>`, columns[1].color) : '',
                over ? row(`Acima da franquia: <strong>${over}</strong>`, columns[2].color) : '',
                units ? row(`Bonificados: <strong>${units}</strong>`, '#D1D5DB') : '',
              ].join('')
              return (
                '<div style="padding:8px 12px;font-size:12px;line-height:1.6;">' +
                `<div style="font-weight:600;margin-bottom:2px;">${label}</div>` +
                `<div>${this.nameValue}: <strong>${count + units}</strong></div>` +
                (units ? `<div>Contabilizados no plano: <strong>${count}</strong></div>` : '') +
                (breakdown ? `<div style="margin:4px 0;">${breakdown}</div>` : '') +
                `<div style="color:#6B7280;">${quotaLine}</div>` +
                '</div>'
              )
            },
          }
        : { enabled: true },
      states: { hover: { filter: { type: 'none' } } },
    }

    this.chart = new ApexCharts(this.element, options)
    this.chart.render()
  }
}
